import React, { useState } from 'react';
import { t } from 'i18next';
import { DataTable } from '../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import {
  PageTrafficMode,
  PageTrafficRow,
} from '../../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { ChatType } from '../../../../shared/types/monitor/ChatType';
import ModelIconsList from './ModelIconsList';
import PageTrafficPrompts from './PageTrafficPrompts';
import useIsMobile from '../../../../shared/hooks/global/UseIsMobile';
import '../../../../shared/components/page-type-tag/PageTypeTag.scss';

interface PageTrafficTableProps {
  rows: PageTrafficRow[];
  mode: PageTrafficMode;
  projectId: number;
  /** Keep the host in the URL column — set when the table spans more than one own domain,
   *  where a bare path cannot be told apart between them. */
  showHost?: boolean;
  /** Filters of the section, forwarded to the per-row prompts request so the expanded list is cut
   *  on the same window and scope the row was classified on. */
  chatType: ChatType | null;
  from: Date | null;
  to: Date | null;
  monitorPromptIds?: number[];
  filterMonitorTypes?: string[];
}

// Desktop only: header + ~8 rows of 56px. The whole ranking is in `rows`, so the table scrolls
// inside the section instead of stretching the Sources page. Phone cards flow freely.
const SCROLL_HEIGHT = '504px';

// Short relative-time formatter — collapses to a single unit with a 1–2 char suffix
// (e.g. "3d", "2w", "2mo", "1y"). The previous version used date-fns `formatDistance`
// which produced "3 days ago" / "about 2 months ago" — too long for narrow date columns
// where the user just wants a glanceable freshness signal.
const formatShortDistance = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return '0s';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  const years = Math.floor(days / 365);
  return `${years}y`;
};

const PageTrafficTable: React.FC<PageTrafficTableProps> = ({
  rows,
  mode,
  projectId,
  showHost = false,
  chatType,
  from,
  to,
  monitorPromptIds,
  filterMonitorTypes,
}) => {
  const isMobile = useIsMobile();
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // Top rank — `rows` is the backend's globally sorted ranking for the current mode (capped, not
  // paginated), so the row's array index + 1 is its true rank.
  const renderRank = (_row: PageTrafficRow, options: { rowIndex: number }) => (
    <span className="page-traffic-table__rank">{options.rowIndex + 1}</span>
  );

  // Renders the URL without the scheme. The host is dropped when every row belongs to the
  // same own domain (it would just repeat), and kept when the table spans several — there a
  // bare `/blog/post` could come from any of them. The text itself is NOT a link; a separate
  // `ico--external-link` icon next to it opens the full URL in a new tab, matching the Top
  // Citation Pages (SourcesStatsTable) pattern.
  const renderUrl = (row: PageTrafficRow) => {
    let pathOnly = row.source;
    try {
      const u = new URL(row.source);
      const path = `${u.pathname}${u.search}${u.hash}` || '/';
      pathOnly = showHost ? `${u.host.replace(/^www\./, '')}${path}` : path;
    } catch {
      // ignore — fall back to raw source string
    }
    return (
      <div className="page-traffic-table__url">
        <span
          className="page-traffic-table__url-text text-ellipsis"
          title={row.title?.trim() || row.source}
        >
          {pathOnly}
        </span>
        <a
          className="page-traffic-table__external-link"
          href={row.source}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          aria-label={t('openInNewTab')}
        >
          <span className="ico--sm ico--external-link" />
        </a>
      </div>
    );
  };

  const renderModels = (row: PageTrafficRow) => (
    <ModelIconsList models={row.models} tooltipKey={row.source} />
  );

  const renderFirstSeen = (row: PageTrafficRow) =>
    row.firstSeenEver ? formatShortDistance(row.firstSeenEver) : '—';

  const renderLastUsed = (row: PageTrafficRow) =>
    row.lastUsedInPeriod ? formatShortDistance(row.lastUsedInPeriod) : '—';

  // Usage count column — primary number (currentCount in current period) + inline tag with
  // the change vs the previous period. The tag is shown for top, topGainers, and topDecliners.
  // It is suppressed for newlyCited (where delta = currentCount and would duplicate the primary
  // number) and droppedOff (where the primary number is already previousCount, so a
  // `-previousCount` tag would just duplicate it). In `top`, brand-new URLs have delta = null
  // (no previous period to compare) so they also render without a tag.
  const renderUsageCount = (row: PageTrafficRow) => {
    // For droppedOff, currentCount is always 0; show previousCount as the "usage count"
    // since that's the number that actually mattered for these URLs.
    const primaryCount = mode === 'droppedOff' ? row.previousCount : row.currentCount;

    let deltaTag: React.ReactNode = null;
    const showDelta =
      mode === 'topGainers' ||
      mode === 'topDecliners' ||
      (mode === 'top' && row.delta !== null);
    if (showDelta) {
      const value = row.delta ?? 0;
      const iconClass =
        value > 0 ? 'ico--chart-up' : value < 0 ? 'ico--chart-down' : 'ico--dot';
      const valueText = value > 0 ? `+${value}` : String(value);
      deltaTag = (
        <div className="tag tag--stats tag--md">
          <span className={iconClass} />
          <span className="tag__value">{valueText}</span>
        </div>
      );
    }

    return (
      <div className="page-traffic-table__usage">
        <span className="page-traffic-table__usage-count">{primaryCount}</span>
        {deltaTag}
      </div>
    );
  };

  // Prompts are not part of `rows` — the expansion mounts PageTrafficPrompts, which fetches them
  // for this URL only when the row is opened.
  const rowExpansionTemplate = (row: PageTrafficRow) => (
    <PageTrafficPrompts
      projectId={projectId}
      source={row.source}
      chatType={chatType}
      from={from}
      to={to}
      monitorPromptIds={monitorPromptIds}
      filterMonitorTypes={filterMonitorTypes}
    />
  );

  return (
    <DataTable
      value={rows}
      dataKey="source"
      expandedRows={expandedRows}
      onRowToggle={(e) => setExpandedRows(e.data as Record<string, boolean>)}
      rowExpansionTemplate={rowExpansionTemplate}
      stripedRows
      scrollable={!isMobile}
      scrollHeight={isMobile ? undefined : SCROLL_HEIGHT}
      tableClassName="table"
      className="table-wrapper page-traffic-table table-wrapper--stack"
      responsiveLayout="stack"
    >
      {/* Telefon: bez pozycji — karty i tak idą w kolejności rankingu. */}
      <Column header={t('top')} body={renderRank} style={{ width: '60px' }} hidden={isMobile} />
      <Column header={t('url')} body={renderUrl} bodyClassName="table__cell--stack-title" />
      <Column header={t('usageCount')} body={renderUsageCount} style={{ width: '140px' }} />
      <Column header={t('models')} body={renderModels} style={{ width: '120px' }} />
      <Column header={t('firstSeen')} body={renderFirstSeen} style={{ width: '90px' }} />
      <Column header={t('lastUsed')} body={renderLastUsed} style={{ width: '90px' }} />
      <Column expander bodyClassName="table__cell--stack-actions" style={{ width: '3rem' }} />
    </DataTable>
  );
};

export default PageTrafficTable;
